// Creator tools: export exactly one loop of a scene as a 1080p60 MP4 (frame-exact via
// WebCodecs + a minimal MP4 muxer; real-time MediaRecorder fallback), or a 1080p PNG.
import { W, H, LOOP, FPS, OUT_W, OUT_H } from './core.js';

/* ================= Minimal MP4 (H.264) muxer ================= */
const u32 = n => new Uint8Array([n >>> 24 & 255, n >>> 16 & 255, n >>> 8 & 255, n & 255]);
const u16 = n => new Uint8Array([n >>> 8 & 255, n & 255]);
const str = s => new Uint8Array([...s].map(ch => ch.charCodeAt(0)));
function cat(parts) {
  let n = 0; for (const p of parts) n += p.length;
  const o = new Uint8Array(n); let k = 0;
  for (const p of parts) { o.set(p, k); k += p.length; }
  return o;
}
const box = (type, ...parts) => { const body = cat(parts); return cat([u32(body.length + 8), str(type), body]); };
const fbox = (type, v, flags, ...parts) => box(type, new Uint8Array([v, flags >> 16 & 255, flags >> 8 & 255, flags & 255]), ...parts);
function u32arr(arr) {
  const o = new Uint8Array(arr.length * 4), dv = new DataView(o.buffer);
  arr.forEach((v, i) => dv.setUint32(i * 4, v));
  return o;
}
export function buildMp4(samples, avcC, w, h, fps) {
  const n = samples.length, TS = fps * 1000, durMs = Math.round(n * 1000 / fps);
  const ftyp = box('ftyp', str('isom'), u32(512), str('isom'), str('iso2'), str('avc1'), str('mp41'));
  let mdatSize = 8; for (const s of samples) mdatSize += s.data.length;
  const offsets = []; let off = ftyp.length + 8;
  for (const s of samples) { offsets.push(off); off += s.data.length; }
  const matrix = u32arr([0x10000, 0, 0, 0, 0x10000, 0, 0, 0, 0x40000000]);
  const mvhd = fbox('mvhd', 0, 0, u32(0), u32(0), u32(1000), u32(durMs), u32(0x10000), u16(0x100), u16(0), u32(0), u32(0), matrix, new Uint8Array(24), u32(2));
  const tkhd = fbox('tkhd', 0, 3, u32(0), u32(0), u32(1), u32(0), u32(durMs), u32(0), u32(0), u16(0), u16(0), u16(0), u16(0), matrix, u32(w * 65536), u32(h * 65536));
  const mdhd = fbox('mdhd', 0, 0, u32(0), u32(0), u32(TS), u32(n * 1000), u16(0x55c4), u16(0));
  const hdlr = fbox('hdlr', 0, 0, u32(0), str('vide'), new Uint8Array(12), str('VideoHandler\0'));
  const avc1 = box('avc1', new Uint8Array(6), u16(1), u16(0), u16(0), new Uint8Array(12), u16(w), u16(h),
    u32(0x480000), u32(0x480000), u32(0), u16(1), new Uint8Array(32), u16(0x18), u16(0xffff), box('avcC', avcC));
  const keys = []; samples.forEach((s, i) => s.key && keys.push(i + 1));
  const stbl = box('stbl',
    fbox('stsd', 0, 0, u32(1), avc1),
    fbox('stts', 0, 0, u32(1), u32(n), u32(1000)),
    fbox('stss', 0, 0, u32(keys.length), u32arr(keys)),
    fbox('stsc', 0, 0, u32(1), u32(1), u32(1), u32(1)),
    fbox('stsz', 0, 0, u32(0), u32(n), u32arr(samples.map(s => s.data.length))),
    fbox('stco', 0, 0, u32(n), u32arr(offsets)));
  const minf = box('minf', fbox('vmhd', 0, 1, new Uint8Array(8)), box('dinf', fbox('dref', 0, 0, u32(1), fbox('url ', 0, 1))), stbl);
  const moov = box('moov', mvhd, box('trak', tkhd, box('mdia', mdhd, hdlr, minf)));
  return new Blob([ftyp, u32(mdatSize), str('mdat'), ...samples.map(s => s.data), moov], { type: 'video/mp4' });
}

/* ================= Encoding ================= */
const fmtTime = s => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const yieldNow = () => new Promise(r => { const ch = new MessageChannel(); ch.port1.onmessage = () => r(); ch.port2.postMessage(0); });
function makeOut() {
  const c = document.createElement('canvas'); c.width = OUT_W; c.height = OUT_H;
  const x = c.getContext('2d'); x.imageSmoothingEnabled = false;
  return [c, x];
}
export function download(blob, name) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = name;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 60000);
}
export async function pickAvc() {
  if (typeof VideoEncoder !== 'function' || typeof VideoFrame !== 'function') return null;
  for (const codec of ['avc1.64002A', 'avc1.4D402A', 'avc1.42E02A', 'avc1.640033']) {
    const cfg = { codec, width: OUT_W, height: OUT_H, bitrate: 12_000_000, framerate: FPS, avc: { format: 'avc' } };
    try { if ((await VideoEncoder.isConfigSupported(cfg)).supported) return cfg; } catch { /* try next */ }
  }
  return null;
}
// Offline and frame-exact: frame i is rendered at t = i / FPS, so the file is exactly one loop.
export async function encodeMp4({ cfg, frames, render, buf, onProgress, isCancelled }) {
  const [out, octx] = makeOut();
  const samples = []; let desc = null, failure = null;
  const enc = new VideoEncoder({
    output: (chunk, meta) => {
      const d = meta && meta.decoderConfig && meta.decoderConfig.description;
      if (!desc && d) desc = ArrayBuffer.isView(d) ? new Uint8Array(d.buffer, d.byteOffset, d.byteLength).slice() : new Uint8Array(d).slice();
      const data = new Uint8Array(chunk.byteLength); chunk.copyTo(data);
      samples.push({ data, key: chunk.type === 'key' });
    },
    error: e => { failure = e; },
  });
  enc.configure(cfg);
  const us = 1e6 / FPS;
  for (let i = 0; i < frames && !isCancelled() && !failure; i++) {
    render(i / FPS);
    octx.drawImage(buf, 0, 0, W, H, 0, 0, OUT_W, OUT_H);
    const vf = new VideoFrame(out, { timestamp: Math.round(i * us), duration: Math.round(us) });
    enc.encode(vf, { keyFrame: i % (FPS * 2) === 0 });
    vf.close();
    while (enc.encodeQueueSize > 6 && !failure) await new Promise(r => {
      const t = setTimeout(r, 30);
      enc.addEventListener('dequeue', () => { clearTimeout(t); r(); }, { once: true });
    });
    if (i % 10 === 0) { onProgress(i / frames); await yieldNow(); }
  }
  if (!failure && !isCancelled()) await enc.flush().catch(e => { failure = e; });
  if (enc.state !== 'closed') enc.close();
  if (failure) throw failure;
  if (isCancelled()) return null;
  if (!desc) throw new Error('encoder returned no avcC');
  return buildMp4(samples, desc, OUT_W, OUT_H, FPS);
}
// Fallback without WebCodecs: real-time capture (4 minutes; keep the tab visible).
export async function recordRealtime({ frames, render, buf, onStatus, isCancelled }) {
  const types = ['video/mp4;codecs=avc1.64002A', 'video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
  const mime = window.MediaRecorder && types.find(tp => MediaRecorder.isTypeSupported(tp));
  if (!mime) throw new Error('no video encoder available in this browser');
  const [out, octx] = makeOut();
  const rec = new MediaRecorder(out.captureStream(FPS), { mimeType: mime, videoBitsPerSecond: 12_000_000 });
  const parts = []; rec.ondataavailable = e => e.data.size && parts.push(e.data);
  const stopped = new Promise(r => { rec.onstop = r; });
  render(0); octx.drawImage(buf, 0, 0, W, H, 0, 0, OUT_W, OUT_H);
  rec.start(1000);
  const start = performance.now();
  await new Promise(resolve => {
    const tick = () => {
      const i = Math.floor((performance.now() - start) * FPS / 1000);
      if (isCancelled() || i >= frames) return resolve();
      render(i / FPS); octx.drawImage(buf, 0, 0, W, H, 0, 0, OUT_W, OUT_H);
      onStatus(`Recording in real time (keep this tab visible) · ${fmtTime(i / FPS)} / ${fmtTime(frames / FPS)}`, i / frames);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
  rec.stop(); await stopped;
  return isCancelled() ? null : new Blob(parts, { type: mime.split(';')[0] });
}

/** Export one full loop and download it. Resolves to { ext, size } or null if cancelled. */
export async function exportLoop({ render, buf, name, onStatus, isCancelled }) {
  const frames = LOOP * FPS, t0 = performance.now();
  onStatus('Preparing encoder…', 0);
  const cfg = await pickAvc();
  const blob = cfg
    ? await encodeMp4({ cfg, frames, render, buf, isCancelled, onProgress: p => {
      const el = (performance.now() - t0) / 1000, eta = p > 0.01 ? el / p - el : 0;
      onStatus(`Rendering MP4 1080p60 · ${(p * 100).toFixed(1)}%${p > 0.01 ? ` · ~${fmtTime(eta)} left` : ''}`, p);
    } })
    : await recordRealtime({ frames, render, buf, onStatus, isCancelled });
  if (!blob) return null;
  const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
  download(blob, `${name}.${ext}`);
  return { ext, size: blob.size };
}

/** Save the frame at loop time t as a 1920x1080 PNG. */
export function savePNG({ render, buf, t, name }) {
  const [out, octx] = makeOut();
  render(t); octx.drawImage(buf, 0, 0, W, H, 0, 0, OUT_W, OUT_H);
  out.toBlob(b => b && download(b, `${name}.png`), 'image/png');
}
